import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import {
  addAdvancedFilter,
  removeAdvancedFilter,
  setContentFilter,
  setPersonalFilter,
  setPurchaseFilter,
} from 'Pods/SearchPage/Store/actions/moreFilters'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  advancedFiltersConfig,
  contentFiltersConfig,
  personalFiltersConfig,
  purchaseFiltersConfig,
} from './constants'

// hideAdvancedLabel drops only the first group's "ADVANCED" title when an external title already
// names the section (mobile collapsible header). The other group labels stay as sub-headings.
function AdvancedFiltersContent({ hideAdvancedLabel = false }) {
  const advancedFilters = useSearchPageStore(state => state.advancedFilters ?? EMPTY_ARRAY)
  const contentFilter = useSearchPageStore(state => state.contentFilter)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  // Personal catalog filters (wishlist, purchased) are backed by ItemsSearch filters that no-op
  // without current_user, so a signed-out viewer would see an "active" filter over unfiltered
  // results. Hidden until pageConfig confirms a logged-in user (client-side; SSR is user-blind).
  const pageConfig = usePageConfig()
  const isLoggedIn = !!pageConfig?.currentUserId

  const handleAdvancedFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addAdvancedFilter(value)
    } else {
      removeAdvancedFilter(value)
    }
  }, [])

  const handleContentFilterChange = useCallback((_, value) => {
    setContentFilter(value)
  }, [])

  const handlePersonalFilterChange = useCallback((_, value) => {
    setPersonalFilter(value)
  }, [])

  const handlePurchaseFilterChange = useCallback((_, value) => {
    setPurchaseFilter(value)
  }, [])

  const selectedContentFilter = useMemo(() => {
    return contentFilter != null ? [contentFilter] : []
  }, [contentFilter])

  const selectedPersonalFilter = useMemo(() => {
    return personalFilter != null ? [personalFilter] : []
  }, [personalFilter])

  const selectedPurchaseFilter = useMemo(() => {
    return purchaseFilter != null ? [purchaseFilter] : []
  }, [purchaseFilter])

  const advancedConfig = useMemo(() => (
    hideAdvancedLabel
      ? [{ ...advancedFiltersConfig[0], label: '' }]
      : advancedFiltersConfig
  ), [hideAdvancedLabel])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={advancedConfig}
        selectedFilters={advancedFilters}
        onFiltersChange={handleAdvancedFilterChange}
        isControlled
        useNewCheckboxes
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={contentFiltersConfig}
        selectedFilters={selectedContentFilter}
        onFiltersChange={handleContentFilterChange}
        hideLastDivider={!isLoggedIn}
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
      {isLoggedIn && (
        <>
          <FiltersSidebar
            isCollapsible={false}
            filters={personalFiltersConfig}
            selectedFilters={selectedPersonalFilter}
            onFiltersChange={handlePersonalFilterChange}
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
          <FiltersSidebar
            isCollapsible={false}
            filters={purchaseFiltersConfig}
            selectedFilters={selectedPurchaseFilter}
            onFiltersChange={handlePurchaseFilterChange}
            hideLastDivider
            hideShowMore
            baseFormWrapperClassName="filter-wrapper__content"
            useRadio
          />
        </>
      )}
    </>
  )
}

AdvancedFiltersContent.propTypes = {
  hideAdvancedLabel: bool,
}

export default memo(AdvancedFiltersContent)
